import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, Send, RotateCcw, Loader2, UserRound, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SID_KEY = "itz_assist_sid", MSG_KEY = "itz_assist_msgs", OWNER_KEY = "itz_assist_owner", HELLO_KEY = "itz_itzi_hello";
const visible = (t) => t.replace(/\[\[[^\]]*\]\]/g, "").replace(/\[\[[^\]]*$/, "").replace(/\*\*/g, "").trimEnd();
const newSid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const SUGGEST = ["Wie sammle ich Punkte?", "Wie funktionieren Duelle?", "Wie löse ich ein Geschenk ein?", "Was bringt das Glücksrad?"];
const HELLO = { role: "assistant", text: "Hi, ich bin Itzi 💖 Dein Helfer für die itzPlay Community. Frag mich alles zu Punkten, Spielen, Geschenken oder deinem Konto!" };

async function streamChat(body, onText) {
  const res = await fetch(`${API}/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Gerade nicht erreichbar");
  const reader = res.body.getReader(), dec = new TextDecoder();
  let buf = "", fin = { escalate: false, links: [] };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n"); buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.t) onText(ev.t);
      if (ev.done) fin = ev;
    }
  }
  return fin;
}

function EscalateForm({ sid, question, user, onDone }) {
  const [f, setF] = useState({ question, email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/assistant/escalate", { session_id: sid, question: f.question, ...(user ? {} : { email: f.email, name: f.name || null }) }); onDone(); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  const inp = "w-full h-10 px-3 rounded-xl border border-pink-200 bg-white text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-rose-300";
  return (
    <form onSubmit={submit} className="rounded-2xl border border-rose-200 bg-white p-3 space-y-2 fade-up" data-testid="assistant-escalate-form">
      <p className="text-xs font-bold text-rose-600 flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" /> Frage an Saskia & Stefan</p>
      <textarea value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} required minLength={3} maxLength={1000} rows={3} data-testid="assistant-escalate-question" className={`${inp} h-auto py-2 resize-none`} />
      {!user && <>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={60} placeholder="Dein Name (optional)" data-testid="assistant-escalate-name" className={inp} />
        <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required placeholder="Deine E-Mail für die Antwort" data-testid="assistant-escalate-email" className={inp} />
      </>}
      <button disabled={busy} data-testid="assistant-escalate-submit" className="w-full h-10 rounded-full pink-gradient text-white text-sm font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} An das Team senden
      </button>
    </form>
  );
}

function Bubble({ m, i, onNav }) {
  const mine = m.role === "user";
  return (
    <div className={`flex flex-col ${mine ? "items-end" : "items-start"} gap-1.5 fade-up`}>
      <p data-testid={`assistant-msg-${i}`} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${mine ? "pink-gradient text-white rounded-br-md" : "bg-white border border-pink-100 rounded-bl-md text-slate-700"}`}>
        {visible(m.text) || <span className="flex gap-1 py-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</span>}
      </p>
      {m.links?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 max-w-[85%]">
          {m.links.map((l, j) => (
            <Link key={l.to} to={l.to} onClick={onNav} data-testid={`assistant-link-${i}-${j}`} className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-white border-2 border-rose-200 text-rose-600 text-xs font-bold hover:bg-rose-50 hover:border-rose-300 active:scale-95 transition-transform fade-up" style={{ animationDelay: `${j * 90}ms` }}>
              {l.label} <ArrowRight className="w-3 h-3" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Hello({ onOpen, onClose }) {
  return (
    <div className="fixed z-50 right-[4.75rem] sm:right-6 bottom-[calc(env(safe-area-inset-bottom)+1rem)] max-lg:[.has-tabbar_&]:bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] sm:bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] w-[210px] sm:w-[260px] itzi-hello" data-testid="assistant-hello">
      <div className="relative bg-white rounded-3xl rounded-br-md shadow-2xl shadow-rose-200/70 border border-pink-100 p-4 pr-9">
        <button onClick={onClose} data-testid="assistant-hello-close" aria-label="Schließen" className="absolute top-2 right-2 p-1 rounded-full text-slate-400 hover:bg-rose-50"><X className="w-4 h-4" /></button>
        <p className="font-extrabold text-sm flex items-center gap-1.5"><span className="itzi-wave inline-block">👋</span> Hi, ich bin Itzi!</p>
        <p className="text-xs text-slate-500 mt-1">Frag mich alles über Punkte, Spiele & Geschenke – ich antworte sofort.</p>
        <button onClick={onOpen} data-testid="assistant-hello-open" className="mt-3 h-8 px-3 rounded-full pink-gradient text-white text-xs font-bold inline-flex items-center gap-1 active:scale-95 transition-transform">Frag mich was <ArrowRight className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

export function AssistantBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || newSid());
  const [msgs, setMsgs] = useState(() => { try { return JSON.parse(localStorage.getItem(MSG_KEY)) || []; } catch { return []; } });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [esc, setEsc] = useState(null);
  const end = useRef(null);
  const { pathname } = useLocation();
  const [hello, setHello] = useState(false);
  useEffect(() => {
    if (user !== false || pathname !== "/" || open || localStorage.getItem(HELLO_KEY)) return;
    const t = setTimeout(() => { localStorage.setItem(HELLO_KEY, "1"); setHello(true); }, 6000);
    return () => clearTimeout(t);
  }, [user, pathname, open]);
  useEffect(() => { if (open || pathname !== "/") setHello(false); }, [open, pathname]);
  useEffect(() => { localStorage.setItem(SID_KEY, sid); }, [sid]);
  useEffect(() => {
    if (!open) return;
    const k = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open]);
  useEffect(() => { localStorage.setItem(MSG_KEY, JSON.stringify(msgs.slice(-30))); }, [msgs]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, esc, open]);
  const owner = user === null ? null : user ? user.user_id : "guest";
  useEffect(() => {
    if (owner === null || localStorage.getItem(OWNER_KEY) === owner) return;
    localStorage.setItem(OWNER_KEY, owner); reset();
  }, [owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setSid(newSid()); setMsgs([]); setEsc(null); };
  const ask = async (q) => {
    const question = q.trim();
    if (!question || busy) return;
    setText(""); setEsc(null); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    const append = (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + t }; return c; });
    try {
      const fin = await streamChat({ session_id: sid, message: question }, append);
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: fin.text ?? visible(c[c.length - 1].text), links: fin.links || [], escalate: fin.escalate, q: question }; return c; });
    } catch (err) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: `${err.message} 🙈`, escalate: true, q: question }; return c; });
    } finally { setBusy(false); }
  };
  const last = msgs[msgs.length - 1];
  const all = [HELLO, ...msgs];

  if (pathname === "/vibes" || pathname === "/menu" || pathname.startsWith("/herz-laeufer") || pathname.startsWith("/zuckerherz") || pathname.startsWith("/zwei-herzen")) return null;
  return (
    <>
      {open && (
        <div role="dialog" aria-label="KI-Support" className="fixed z-50 inset-x-2 bottom-24 max-lg:[.has-tabbar_&]:bottom-40 top-[calc(env(safe-area-inset-top)+4.5rem)] sm:inset-x-auto sm:top-auto sm:right-6 sm:bottom-24 sm:w-[380px] sm:h-[560px] bg-white rounded-3xl shadow-2xl shadow-rose-200/60 border border-pink-100 flex flex-col overflow-hidden fade-up" data-testid="assistant-panel">
          <div className="flex items-center gap-2.5 px-3 h-16 shrink-0 pink-gradient text-white">
            <span className="relative w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center"><Sparkles className="w-5 h-5" /><span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-rose-500" /></span>
            <div className="flex-1 min-w-0"><p className="font-extrabold leading-tight">Itzi · KI-Support</p><p className="text-[11px] text-white/85">Antwortet sofort – rund um die Uhr</p></div>
            {msgs.length > 0 && <button onClick={reset} data-testid="assistant-reset" title="Neue Unterhaltung" aria-label="Neue Unterhaltung" className="p-1.5 rounded-full hover:bg-white/20"><RotateCcw className="w-4 h-4" /></button>}
            <button onClick={() => setOpen(false)} data-testid="assistant-close" aria-label="Schließen" className="p-1.5 rounded-full hover:bg-white/20"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-rose-50/30" data-testid="assistant-thread">
            {all.map((m, i) => <Bubble key={i} m={m} i={i} onNav={() => setOpen(false)} />)}
            {msgs.length === 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="assistant-suggestions">
                {SUGGEST.map((s, i) => <button key={s} onClick={() => ask(s)} data-testid={`assistant-suggest-${i}`} className="px-3 py-1.5 rounded-full bg-white border border-rose-200 text-xs font-semibold text-rose-600 hover:bg-rose-50 active:scale-95 transition-transform">{s}</button>)}
              </div>
            )}
            {!busy && last?.escalate && esc === null && (
              <button onClick={() => setEsc("form")} data-testid="assistant-escalate-btn" className="w-full h-10 rounded-full border-2 border-rose-300 text-rose-600 text-sm font-bold hover:bg-rose-50 inline-flex items-center justify-center gap-2 fade-up"><UserRound className="w-4 h-4" /> Frage an das Team weiterleiten</button>
            )}
            {esc === "form" && <EscalateForm sid={sid} question={last?.q || ""} user={user} onDone={() => setEsc("sent")} />}
            {esc === "sent" && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-2xl p-3 flex gap-2 fade-up" data-testid="assistant-escalate-sent"><CheckCircle2 className="w-4 h-4 shrink-0" /> Danke! Deine Frage ist bei Saskia & Stefan angekommen. {user ? "Die Antwort bekommst du in deinen Mitteilungen 💌" : "Die Antwort kommt per E-Mail 💌"}</p>}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(text); }} className="flex items-center gap-1.5 p-2 border-t border-pink-100">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Stell deine Frage …" data-testid="assistant-input" className="flex-1 min-w-0 h-10 px-4 rounded-full border border-pink-200 bg-white text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" />
            <button disabled={busy || !text.trim()} data-testid="assistant-send" aria-label="Senden" className="w-10 h-10 shrink-0 rounded-full pink-gradient text-white flex items-center justify-center disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
          </form>
          <p className="text-[10px] text-center text-slate-400 pb-1.5">KI-Antworten können Fehler enthalten.</p>
        </div>
      )}
      {hello && !open && <Hello onOpen={() => { setHello(false); setOpen(true); }} onClose={() => setHello(false)} />}
      <button onClick={() => setOpen((o) => !o)} data-testid="assistant-bubble" aria-label="KI-Support öffnen"
        className="fixed z-50 right-4 sm:right-6 bottom-[calc(env(safe-area-inset-bottom)+1rem)] max-lg:[.has-tabbar_&]:bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] w-14 h-14 rounded-full pink-gradient text-white shadow-xl shadow-rose-300/60 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
        {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
        {!open && <span className="absolute inset-0 rounded-full ring-4 ring-rose-300/50 soft-ping pointer-events-none" />}
      </button>
    </>
  );
}
